import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { AppProvider, useApp } from './contexts/AppContext';
import { HomeScreenProvider, useHomeScreen } from './contexts/HomeScreenContext';
import { Layout } from './components/Layout';
import { HomePage } from './pages/HomePage';
import { MySessionPage } from './pages/MySessionPage';
import { ChargerTablePage } from './pages/ChargerTablePage';
import { ActivityPage } from './pages/ActivityPage';
import { AuthPage } from './pages/AuthPage';
import { AdminPage } from './pages/AdminPage';
import { LoadingPage } from './pages/LoadingPage';
import { StartChargingPage } from './pages/StartChargingPage';
import { PostponeQueuePage } from './pages/PostponeQueuePage';
import PasswordRegistrationPage from './pages/PasswordRegistrationPage';
import ProfilePage from './pages/ProfilePage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { initializeI18n } from './i18n';
import { type AppBootstrap } from './utils/appBootstrap';

// Home Screen redirect component - redirects to user's preferred home screen
// ONLY on initial app load, not when user explicitly navigates via menu
function HomeScreenRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const { getHomeRoute } = useHomeScreen();

  useEffect(() => {
    // Check if this is an explicit navigation (user clicked menu item)
    // Location state will contain "fromMenu" flag set by Navigation component
    const isExplicitNavigation = location.state?.fromMenu === true;
    
    // Only redirect from root path when:
    // 1. User is at root path
    // 2. This is NOT an explicit menu navigation
    // 3. User has a preferred home screen different from root
    if (location.pathname === '/' && !isExplicitNavigation) {
      const homeRoute = getHomeRoute();
      if (homeRoute !== '/' && homeRoute !== location.pathname) {
        console.log('🏠 HomeScreenRedirect: Initial load - navigating to preferred home screen:', homeRoute);
        navigate(homeRoute, { replace: true });
      }
    } else if (isExplicitNavigation) {
      console.log('🏠 HomeScreenRedirect: Explicit menu navigation detected - staying on', location.pathname);
    }
  }, [navigate, location.pathname, location.state, getHomeRoute]);

  return null;
}

// Early redirect component based on bootstrap data
function EarlyRedirect({ bootstrap }: { bootstrap: AppBootstrap }) {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (bootstrap.targetRoute && location.pathname !== bootstrap.targetRoute) {
      console.log('🚀 EarlyRedirect: Navigating from', location.pathname, 'to', bootstrap.targetRoute);
      navigate(bootstrap.targetRoute, { replace: true });
    } else if (location.pathname === bootstrap.targetRoute) {
      console.log('🚀 EarlyRedirect: Already at target route', bootstrap.targetRoute, '- rendering normally');
    }
  }, [bootstrap.targetRoute, navigate, location.pathname]);
  
  // If we're already at the target route, don't show loading
  if (location.pathname === bootstrap.targetRoute) {
    return null; // Let normal routing take over
  }
  
  // Show loading while redirecting
  return <LoadingPage />;
}

// Protected Route component that redirects to /auth if not authenticated
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { state } = useApp();
  
  if (state.isLoading) {
    return <LoadingPage />;
  }
  
  if (!state.isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }
  
  return <>{children}</>;
}

function HashNavigationHandler() {
  const navigate = useNavigate();

  // Debug logging helper
  const addDebugLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    const log = `[${timestamp}] ${message}`;
    const existingLogs = JSON.parse(localStorage.getItem('debugLogs') || '[]');
    const newLogs = [log, ...existingLogs.slice(0, 19)]; // Keep last 20 logs
    localStorage.setItem('debugLogs', JSON.stringify(newLogs));
    
    // Trigger a custom event to notify HomePage about new logs
    window.dispatchEvent(new CustomEvent('debugLogAdded', { detail: newLogs }));
  };

  useEffect(() => {
    const handleHashNavigation = () => {
      const hash = window.location.hash.substring(1); // Remove the # character
      
      addDebugLog(`🔗 HashNavigationHandler - Processing hash: "${hash}"`);
      console.log('🔗 HashNavigationHandler - Processing hash:', hash);
      
      // Parse hash for deeplink actions from Telegram
      if (hash) {
        const [action, queryString] = hash.split('?');
        
        addDebugLog(`🎯 Action: "${action}", QueryString: "${queryString}"`);
        console.log('🎯 Action:', action, 'QueryString:', queryString);
        
        switch (action) {
          case 'home':
            navigate('/');
            break;
          case 'my-session':
            navigate('/session');
            break;
          case 'chargers':
            navigate('/');
            break;
          case 'charger-details':
            // Parse chargerId from query parameters if needed
            if (queryString) {
              const params = new URLSearchParams(queryString);
              const chargerId = params.get('chargerId');
              if (chargerId) {
                navigate(`/?charger=${chargerId}`);
              } else {
                navigate('/');
              }
            } else {
              navigate('/');
            }
            break;
          case 'start-charging':
            // Navigate to dedicated start charging page
            if (queryString) {
              const params = new URLSearchParams(queryString);
              const chargerId = params.get('chargerId');
              if (chargerId) {
                navigate(`/start-charging/${chargerId}`);
              } else {
                navigate('/');
              }
            } else {
              navigate('/');
            }
            break;
          case 'postpone':
            // Navigate to postpone queue page
            if (queryString) {
              const params = new URLSearchParams(queryString);
              const chargerId = params.get('chargerId');
              if (chargerId) {
                navigate(`/postpone-queue/${chargerId}`);
              } else {
                navigate('/');
              }
            } else {
              navigate('/');
            }
            break;
          case 'queue':
            // Parse chargerId from query parameters if needed
            if (queryString) {
              const params = new URLSearchParams(queryString);
              const chargerId = params.get('chargerId');
              if (chargerId) {
                navigate(`/?charger=${chargerId}`);
              } else {
                navigate('/');
              }
            } else {
              navigate('/');
            }
            break;
          default:
            // Unknown action, navigate to home
            navigate('/');
            break;
        }
        
        // Clear the hash after processing to prevent browser back button issues
        window.history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    };

    // Handle hash navigation on initial load
    handleHashNavigation();

    // Listen for hash changes (though this is less common in Telegram Mini Apps)
    window.addEventListener('hashchange', handleHashNavigation);

    return () => {
      window.removeEventListener('hashchange', handleHashNavigation);
    };
  }, [navigate]);

  return null;
}

// App component props
interface AppProps {
  bootstrap: AppBootstrap | null;
}

function App({ bootstrap }: AppProps) {
  useEffect(() => {
    // Initialize i18n with default language (already done in main.tsx)
    // This is kept for backward compatibility in case of bootstrap failure
    if (!bootstrap) {
      initializeI18n().catch(console.error);
    }
  }, [bootstrap]);

  // If we have bootstrap data and need early redirect, handle it
  // But only for routes that are NOT /auth (let normal routing handle /auth)
  if (bootstrap?.shouldRedirect && bootstrap.targetRoute && bootstrap.targetRoute !== '/auth' && window.location.pathname !== bootstrap.targetRoute) {
    return (
      <ErrorBoundary>
        <AppProvider>
          <HomeScreenProvider>
            <Router>
              <EarlyRedirect bootstrap={bootstrap} />
            </Router>
          </HomeScreenProvider>
        </AppProvider>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary>
      <AppProvider>
        <HomeScreenProvider>
          <Router>
            <HashNavigationHandler />
            <Layout>
              <Routes>
                {/* Public authentication routes */}
                <Route path="/auth" element={<AuthPage />} />
                <Route path="/register-password" element={<PasswordRegistrationPage />} />
                
                {/* Protected routes - redirect to /auth if not authenticated */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <HomeScreenRedirect />
                    <HomePage />
                  </ProtectedRoute>
                } />
                <Route path="/session" element={
                  <ProtectedRoute>
                    <MySessionPage />
                  </ProtectedRoute>
                } />
                <Route path="/charger-table" element={
                  <ProtectedRoute>
                    <ChargerTablePage />
                  </ProtectedRoute>
                } />
                <Route path="/activity" element={
                  <ProtectedRoute>
                    <ActivityPage />
                  </ProtectedRoute>
                } />
                <Route path="/profile" element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                } />
                <Route path="/admin" element={
                  <ProtectedRoute>
                    <AdminPage />
                  </ProtectedRoute>
                } />
                <Route path="/start-charging/:chargerId" element={
                  <ProtectedRoute>
                    <StartChargingPage />
                  </ProtectedRoute>
                } />
                <Route path="/postpone-queue/:chargerId" element={
                  <ProtectedRoute>
                    <PostponeQueuePage />
                  </ProtectedRoute>
                } />
                
                {/* Legacy routes - redirect to new auth system */}
                <Route path="/register" element={<Navigate to="/auth" replace />} />
                <Route path="/loading" element={<LoadingPage />} />
                
                {/* Fallback - redirect unknown routes to auth */}
                <Route path="*" element={<Navigate to="/auth" replace />} />
              </Routes>
            </Layout>
          </Router>
        </HomeScreenProvider>
      </AppProvider>
    </ErrorBoundary>
  );
}

export default App;